import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ConsentState {
  // Basic
  consentName: string;
  error: string;
  loading: boolean;
  editable: boolean;
  formSubmitting: boolean;

  status: string;
  formDirty: boolean; // Unused X

  // Modal Props
  showPropModal: boolean;
  modalConsent: any;
  modalIsEdit: boolean;

  // Tab Validation
  isBasicError: string;

  // Currently In Use
  blocks: any[];
  formId: string; //FormId
  studyId: string;
}

export const initialQuestionState: ConsentState = {
  // Basic
  consentName: "",
  error: "",
  loading: true,
  editable: true,
  formSubmitting: false,

  status: "",
  formDirty: false, // Unused X

  //Modal Props
  showPropModal: false,
  modalConsent: null,
  modalIsEdit: false,

  // Tab Validation
  isBasicError: "",

  // Currently In Use
  blocks: [],
  formId: "", //FormId
  studyId: "",
};

export const consentSlice = createSlice({
  name: "consent",
  initialState: initialQuestionState,
  reducers: {
    reset: () => initialQuestionState,
    setConsentLoader: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setConsentSubmitting: (state, action: PayloadAction<boolean>) => {
      state.formSubmitting = action.payload;
    },
    resetConsentState: (state) => {
      state.consentName = "";
      state.error = "";
      state.loading = true;
      state.editable = true;
      state.formSubmitting = false;

      state.status = "";
      state.formDirty = false;

      state.showPropModal = false;
      state.modalConsent = null;
      state.modalIsEdit = false;

      state.blocks = [];
      state.formId = "";
      state.studyId = "";
    },
    setConsentsDetails: (state, action: PayloadAction<ConsentState>) => {
      state.consentName = action.payload.consentName;
      state.error = action.payload.error;
      state.loading = action.payload.loading;
      state.editable = action.payload.editable;
      state.formSubmitting = action.payload.formSubmitting;

      state.status = action.payload.status;
      state.formDirty = action.payload.formDirty;

      state.showPropModal = action.payload.showPropModal;
      state.modalConsent = action.payload.modalConsent;
      state.modalIsEdit = action.payload.modalIsEdit;

      state.blocks = action.payload.blocks;
      state.formId = action.payload.formId;
      state.studyId = action.payload.studyId;
    },

    setConsentModalDetails: (
      state,
      action: PayloadAction<{
        show: boolean;
        block: any;
        modalIsEdit: boolean;
      }>
    ) => {
      state.showPropModal = action.payload.show;
      state.modalConsent = action.payload.block;
      state.modalIsEdit = action.payload.modalIsEdit;
    },

    handleTabErrors: (
      state,
      action: PayloadAction<{
        errorHandler: {
          basic: string;
        };
      }>
    ) => {
      const { basic } = action.payload.errorHandler;

      state.isBasicError = basic;
    },

    handleBasicsChange: (
      state,
      action: PayloadAction<{
        key: string;
        value: any;
      }>
    ) => {
      const { key, value } = action.payload;
      if (state.modalConsent.config && key === "placeholder") {
        state.modalConsent.config[key] = value;
      } else {
        state.modalConsent[key] = value;
      }
    },
    handlePlaceholderChange: (
      state,
      action: PayloadAction<{
        placeholderKey: string;
        value: any;
      }>
    ) => {
      const { placeholderKey, value } = action.payload;
      if (state.modalConsent.config.placeholders) {
        state.modalConsent.config.placeholders[placeholderKey] = value;
      } else {
        state.modalConsent.config.placeholders = {
          [placeholderKey]: value,
        };
      }
    },
    handleBasicsSwitchChange: (
      state,
      action: PayloadAction<{
        key: string;
        value: boolean | string;
      }>
    ) => {
      const { key, value } = action.payload;

      if (state.modalConsent.config) {
        state.modalConsent.config[key] = value;
      } else {
        state.modalConsent.config = {
          key: value,
        };
      }
      if (key === "date") {
        if (value) {
          state.modalConsent.config.format = "DD/MM/YYYY";
        } else {
          delete state.modalConsent.config["format"];
        }
      }
    },
    handleTypeChange: (
      state,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      const { value } = action.payload;

      if (state.modalConsent.config) {
        state.modalConsent.config["type"] = value;
      } else {
        state.modalConsent.config = {
          type: value,
        };
      }

      if (value === "text") {
        delete state.modalConsent.config.format;
        state.modalConsent.config.allowMultiline = false;
      } else if (value === "date") {
        delete state.modalConsent.config.allowMultiline;
        state.modalConsent.config.format = "DD/MM/YYYY";
      }
    },
    handleChoiceModification: (
      state,
      action: PayloadAction<{
        index: number;
        section: "key" | "label" | "description";
        value: any;
      }>
    ) => {
      const { index, section, value } = action.payload;
      state.modalConsent.config.choices.forEach((c: any, currIndex: number) => {
        if (currIndex === index) {
          let val = value;
          c[section] = val;
        }
      });
    },

    handleAddRemoveChoices: (
      state,
      action: PayloadAction<{
        actionType: "add" | "remove";
        actionIndex: number;
      }>
    ) => {
      const { actionType, actionIndex } = action.payload;
      let currBlockChoices = state.modalConsent.config.choices;

      const newOpt = {
        key: "",
        label: "",
        description: "",
      };

      if (actionType === "add") {
        currBlockChoices.push({
          ...newOpt,
        });
      } else if (actionType === "remove") {
        currBlockChoices.splice(actionIndex, 1);
      }

      state.modalConsent.config.choices = currBlockChoices;
    },
  },
});

export const {
  setConsentsDetails,
  setConsentLoader,
  setConsentSubmitting,
  setConsentModalDetails,
  handleTabErrors,
  handleBasicsChange,
  resetConsentState,
  handleBasicsSwitchChange,
  handleTypeChange,
  handleChoiceModification,
  handleAddRemoveChoices,
  handlePlaceholderChange,
  reset,
} = consentSlice.actions;

export default consentSlice.reducer;
